<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import FormButton from '../../components/Atoms/FormButton.vue';
import Spinner from "@/components/ui/Spinner.vue"
import StreamSourceDropdown from "@/components/ui/StreamSourceDropdown.vue"
import MultiviewStreamPreview from "@/components/Multiview/MultiviewStreamPreview.vue"
import MultiviewService from '@/services/MultiviewService'
import { useNotification } from "@kyvg/vue3-notification"
import IconBase from '../../components/icon/IconBase.vue';
import BackButton from '@/components/Atoms/BackButton.vue'
import IconLowLatencyMode from '../../components/icon/IconLowLatencyMode.vue';
import IconEmbedPlayerMode from '../../components/icon/IconEmbedPlayerMode.vue';
import IconMultiviewLayout from '../../components/icon/IconMultiviewLayout.vue';
import IconBin from '../../components/icon/IconBin.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import SelectLayoutModal from '@/components/modals/SelectLayoutModal.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue';
import Title from "@/components/ui/Title.vue"
import IconMultiviewLockedPlaceholderImage from '../../components/icon/IconMultiviewLockedPlaceholderImage.vue'
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue';
import FormSwitch from "@/components/Atoms/FormSwitch.vue"
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconElipsisVertical from '../../components/icon/IconElipsisVertical.vue'
import HeaderHelperLinks from '@/components/Moleculas/HeaderHelperLinks.vue'
import { ref, computed, onMounted, onUnmounted, getCurrentInstance, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { track, EVENTS } from '@/lib/tracking'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { notify } = useNotification()

const instance = getCurrentInstance();
const root = instance.proxy.$root;

const multiview = computed(() => store.getters['Multiview/multiview'])
const multiviewReady = computed(
	() => String(multiview.value?._id || '') === String(route.params.id || '')
)
const detailResolving = ref(true)
const detailSyncing = ref(false)
const detailRequestToken = ref(0)
const scopeAlive = ref(true)
const maxMultiviews = computed(() => store.getters['User/maxMultiviews'])
const isLiveSubscriptionExpired = computed(() => store.getters['User/isLiveSubscriptionExpired'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const teamPermissionError = computed(() => store.getters['Multiview/teamPermissionError'])
const userEmail = computed(() => store.getters['User/userEmail'])

const streamSourceDropdownVisible = ref(false)
const openSourceDropdownIndex = ref(null)
const modalDEMOVideo = ref(null)
const bitrateGraph = ref(null)
const playerType = ref('ll')
const mobileActionsMenu = ref(null)

// Match dashboard ViewModeSwitcher segmented control look
const playerModeBtnBase = 'h-7 w-7 flex items-center justify-center rounded transition-colors duration-150'
const playerModeBtnActive = 'bg-surface-5 text-white shadow-sm'
const playerModeBtnInactive = 'bg-transparent text-surface-7 hover:text-surface-9 hover:bg-surface-4'

const loadMultiview = async (id) => {
	const token = ++detailRequestToken.value
	const isCurrentRequest = () => (
		scopeAlive.value
		&& token === detailRequestToken.value
		&& String(route.params.id) === String(id)
	)
	let seedSnapshot = null

	detailResolving.value = true
	detailSyncing.value = true
	store.dispatch('Multiview/setTeamPermissionError', { error: false })

	const seedFromList = async () => {
		if (!isCurrentRequest() || multiviewReady.value) return false
		const seeded = await store.dispatch('Multiview/seedMultiview', id)
		if (!seeded || !isCurrentRequest()) return false
		seedSnapshot = JSON.stringify(multiview.value)
		detailResolving.value = false
		return true
	}

	await seedFromList()

	void store.dispatch('Streams/getStreams').catch((err) => {
		if (isCurrentRequest()) console.error('Failed to load streams', err)
	})
	void store.dispatch('Multiview/getMultiviews').then(() => seedFromList()).catch((err) => {
		if (isCurrentRequest()) console.error('Failed to synchronize Multiviews', err)
	})

	try {
		const freshMultiview = await store.dispatch('Multiview/fetchMultiview', id)
		if (!isCurrentRequest()) return

		const seededStateUnchanged = seedSnapshot !== null
			&& JSON.stringify(multiview.value) === seedSnapshot
		if (seedSnapshot === null || seededStateUnchanged) {
			await store.dispatch('Multiview/applyMultiview', freshMultiview)
		}
		detailResolving.value = false
	} catch (err) {
		if (!isCurrentRequest()) return
		const permissionDenied = err && err.toString().includes('not have access to')
		if (permissionDenied) {
			store.dispatch('Multiview/setTeamPermissionError', { error: true })
			detailResolving.value = false
		} else if (!multiviewReady.value) {
			await router.push({ name: 'MultiviewDashboard' })
		} else {
			detailResolving.value = false
		}
		notify({
			group: "error",
			text: err || "Multiview load failed",
		});
	} finally {
		if (isCurrentRequest()) detailSyncing.value = false
	}
}

onMounted(() => {
	void loadMultiview(route.params.id)
})

watch(
	() => route.params.id,
	(id, previousId) => {
		if (previousId !== undefined && String(id) !== String(previousId)) {
			void loadMultiview(id)
		}
	}
)

onUnmounted(() => {
	scopeAlive.value = false
	detailRequestToken.value += 1
})

const onSourceDropdownShow = (index) => {
	streamSourceDropdownVisible.value = true
	openSourceDropdownIndex.value = index
}

const onSourceDropdownHide = () => {
	streamSourceDropdownVisible.value = false
	openSourceDropdownIndex.value = null
}

const switchPlayer = (typ) => {
	playerType.value = typ
}

const onSourceStreamSelected = async (stream) => {
	const streamObj = {
		_id: stream?._id,
		key: stream?.key,
		enabled: stream?.enabled,
		name: stream?.name
	}
	const index = Number(stream.refLink)
	const streams = _.map(multiview.value.streams, (item) => {
		return item?._id ?? null
	})

	streams[index] = streamObj?._id ?? null

	try {
		await MultiviewService.updateMultiviews(
			multiview.value._id,
			{
				layout: multiview.value.layout,
				streams: streams
			}
		);

		store.dispatch('Multiview/setSource', {
			id: multiview.value._id,
			index,
			stream: streamObj?._id ? streamObj : null
		})

		track(EVENTS.MULTIVIEW_CONFIGURED, {
			multiviewId: multiview.value._id,
			filledSourceCount: streams.filter(Boolean).length,
		})

		notify({
			group: "success",
			text: `Stream source ${streamObj?._id ? 'updated' : 'removed'}`,
		});
	} catch (err) {
		notify({
			group: "error",
			text: err.message || "Multiview source update failed",
		});
	}
}

const toggleMultiview = async () => {
	const willEnable = !multiview.value.enabled
	try {
		await MultiviewService.toggleMultiview(
			multiview.value._id,
		);

		store.dispatch('Multiview/toggleMultiview', { id: multiview.value._id })

		track(EVENTS.MULTIVIEW_TOGGLED, {
			multiviewId: multiview.value._id,
			enabled: willEnable,
		})

		notify({
			group: "success",
			text: `Multiview ${willEnable ? 'enabled' : 'disabled'}`
		});
	} catch (err) {
		notify({
			group: "error",
			text: err.message || "Multiview source update failed",
		});
	}
}

const closeMobileActionsMenu = () => {
	mobileActionsMenu.value?.hide?.()
}

const onMobileToggleMultiview = () => {
	closeMobileActionsMenu()
	toggleMultiview()
}

const onMobileDeleteMultiview = () => {
	closeMobileActionsMenu()
	onDeleteMultiviewRequest()
}

const onMultiviewDelete = async () => {
	if (!multiview.value._id) {
		return
	}

	try {
		await MultiviewService.deleteMultiview(multiview.value._id)
		store.dispatch('Multiview/removeMultiview', { id: multiview.value._id })
		notify({
			group: "success",
			text: "Multiview deleted",
		});

		router.push({
			name: "MultiviewDashboard",
		})
	} catch (err) {
		notify({
			group: "error",
			text: err.message || "Unable to delete the multiview.",
		});
	}
}

const onDeleteMultiviewRequest = (id) => {
	showModal('delete-multiview-content')
}

const onChangeLayoutRequest = (id) => {
	showModal('change-layout')
}

const showDemo = () => {
	modalDEMOVideo.value.show()
}

const onLayoutResize = async (newLayout) => {
	const currentLayout = multiview.value.layout
	let streams = multiview.value.streams
	if (newLayout < currentLayout) {
		streams = streams.slice(0, newLayout)
	} else {
		streams = streams.concat(Array(newLayout - streams.length).fill(null));
	}

	try {
		await MultiviewService.updateMultiviews(
			multiview.value._id,
			{
				layout: newLayout,
				streams: streams,
			}
		);

		store.dispatch('Multiview/setLayout', { id: multiview.value._id, streams, layout: newLayout })

		track(EVENTS.MULTIVIEW_CONFIGURED, {
			multiviewId: multiview.value._id,
			layout: newLayout,
		})

		notify({
			group: "success",
			text: `Multiview layout updated.`
		});
	} catch (err) {
		notify({
			group: "error",
			text: err.message || "Unable to update the multiview layout.",
		});
	}

}

const onSourceStreamEdit = async ({ stream, refLink }) => {
	await onSourceStreamSelected({ refLink })
}

const saveMultiviewName = async (name) => {
	try {
		await MultiviewService.updateMultiviews(
			multiview.value._id,
			{
				layout: multiview.value.layout,
				name: name,
			}
		);

		store.dispatch('Multiview/setName', { id: multiview.value._id, name })

		notify({
			group: "success",
			text: `Multiview name changed`
		});
	} catch (err) {
		notify({
			group: "error",
			text: err.message || "Multiview name update failed",
		});
	}
}

</script>

<template>
	<div class="w-full container detail-shell px-4 mt-4 lg:pt-8 lg:self-start"
		:data-detail-syncing="detailSyncing || undefined">
		<Spinner v-if="detailResolving || (!multiviewReady && !teamPermissionError)" text="Retrieving data..." classes="text-surface-8 mt-96"
			spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<main v-else-if="!hasLiveSubscription || isLiveSubscriptionExpired || maxMultiviews === 0 || teamPermissionError">
			<header v-if="!teamPermissionError" class="flex items-center mb-1 mt-5">
				<BackButton to="/multiview" class="mr-2" />

				<Title :editable="false" :title="multiview?.name" @changed="saveMultiviewName" />

			</header>
			<HeaderHelperLinks v-if="!teamPermissionError" howItWorksLink="https://docs.castr.com/en/articles/10363776-multiview-monitoring-multiple-livestreams-at-once" howItWorksText="How Multiview works" tutorialLink="cm5nfhg2e05nqm5nfs3y3h7j1" />
			
			<section class="text-center mt-48 items-center">
				<div class="flex flex-col items-center">
					<icon-base width-view-box="130" height-view-box="90" width="130" height="90" iconColor="none" class="mb-3">
						<icon-multiview-locked-placeholder-image />
					</icon-base>
				</div>

				<p v-if="teamPermissionError" class="text-sm text-surface-7 mb-3">
					You do not have permission to access this feature.<br>
					Please contact
					<Tooltip class="email-tooltip text-underline text-dark-9 text-500 text-md" :content="userEmail">
						team owner
					</Tooltip>
					to request access.
				</p>
				<p v-else class="text-sm text-surface-7 mb-3">Your current plan does not support this feature. <br>Please
					upgrade to get full access to multiview </p>

				<p v-if="!teamPermissionError" class="flex flex-col w-full items-center">
					<FormButton to="/subscribe" class="w-64 justify-center">
						Upgrade Plan
					</FormButton>
				</p>
			</section>
		</main>

		<div v-else class="w-full !px-4 md:!px-0 justify-self-start ml-0">
			<main class="min-w-0 md:pl-4">
				<header class="mb-3 mt-5 flex flex-col gap-3 md:mb-1">
					<div class="flex min-w-0 items-center gap-2">
						<BackButton to="/multiview" class="!hidden md:!inline-flex" />

						<div class="min-w-0 flex-1">
							<Title :editable="true" :title="multiview?.name" @changed="saveMultiviewName" />
						</div>

						<!-- Mobile: Enable/Disable + Delete -->
						<DropDownMenu
							ref="mobileActionsMenu"
							is-icon
							position="right"
							type="secondary"
							no-border
							class="shrink-0 md:hidden"
						>
							<template #toggle-button>
								<icon-base class="text-surface-7">
									<IconElipsisVertical />
								</icon-base>
							</template>
							<template #menu-items>
								<DropDownMenuItem @click="onMobileToggleMultiview">
									{{ multiview.enabled ? 'Disable' : 'Enable' }}
								</DropDownMenuItem>
								<DropDownMenuItem @click="onMobileDeleteMultiview">
									Delete
								</DropDownMenuItem>
							</template>
						</DropDownMenu>
					</div>

					<div class="flex w-full flex-wrap items-center justify-start gap-2 md:justify-end">
						<div class="flex min-w-0 shrink-0 items-center text-tiny text-surface-9 sm:w-48">
							<FormSwitch size="md" label="Show bitrate graph" labelPosition="right" v-model="bitrateGraph" />
						</div>

						<ul class="flex shrink-0 items-center gap-0.5 rounded-md bg-surface-3 p-0.5 sm:mr-1">
							<li>
								<Tooltip content="Low latency mode">
									<FormButton
										isIcon
										@click="switchPlayer('ll')"
										:customClasses="[playerModeBtnBase, playerType === 'll' ? playerModeBtnActive : playerModeBtnInactive]"
									>
										<icon-base :width="16" :height="16" fill="none">
											<icon-low-latency-mode />
										</icon-base>
									</FormButton>
								</Tooltip>
							</li>
							<li>
								<Tooltip content="Embed player mode">
									<FormButton
										isIcon
										@click="switchPlayer('embed')"
										:customClasses="[playerModeBtnBase, playerType === 'embed' ? playerModeBtnActive : playerModeBtnInactive]"
									>
										<icon-base :width="16" :height="16" fill="none">
											<icon-embed-player-mode />
										</icon-base>
									</FormButton>
								</Tooltip>
							</li>
						</ul>

						<FormButton @click.prevent="onChangeLayoutRequest" type="secondary" size="sm" class="shrink-0">
							<icon-base>
								<icon-multiview-layout />
							</icon-base>
							<span class="ml-2">Layout</span>
						</FormButton>

						<!-- Desktop: Enable/Disable + Delete -->
						<FormButton
							v-if="multiview.enabled"
							@click.prevent="toggleMultiview()"
							type="secondary"
							size="sm"
							class="hidden md:inline-flex"
						>
							Disable
						</FormButton>
						<FormButton
							v-else
							@click.prevent="toggleMultiview()"
							type="main"
							size="sm"
							class="hidden md:inline-flex"
						>
							Enable
						</FormButton>
						<FormButton
							@click.prevent="onDeleteMultiviewRequest()"
							type="danger"
							size="sm"
							isIcon
							class="hidden md:inline-flex"
						>
							<icon-base>
								<icon-bin />
							</icon-base>
						</FormButton>
					</div>
				</header>

				<HeaderHelperLinks class="mb-6 mt-6 !hidden md:!flex md:mt-0" v-if="!teamPermissionError" howItWorksLink="https://docs.castr.com/en/articles/10363776-multiview-monitoring-multiple-livestreams-at-once" howItWorksText="How Multiview works" tutorialLink="cm5nfhg2e05nqm5nfs3y3h7j1" />

				<section>
					<ul class="grid grid-cols-1 xl:grid-cols-2 gap-1 xl:gap-4">
						<li
							class="min-w-0"
							:class="{ 'relative z-20': openSourceDropdownIndex === index }"
							v-for="(stream, index) in multiview.streams"
							:key="index"
						>
							<div
								class="relative flex aspect-video w-full items-center justify-center"
								:class="[
									(!stream || !multiview.enabled || bitrateGraph) ? 'bg-surface-2' : 'bg-black',
									// Clip video/chart content only; keep overflow visible so source dropdown isn't cut off.
									(!stream || !multiview.enabled) ? 'overflow-visible' : 'overflow-hidden',
								]"
							>
								<div
									v-if="(!stream || !multiview.enabled)"
									class="flex w-full items-center justify-center px-6"
								>
									<!-- Compact centered "Select source" control (matches mobile Multiview mock). -->
									<div class="w-full max-w-[220px]">
										<Tooltip wrapperClass="w-full" v-if="!multiview.enabled" :content="'Enable to continue using Multiview'">
											<stream-source-dropdown @on-show="() => onSourceDropdownShow(index)" @on-hide="onSourceDropdownHide"
												@on-source-selected="onSourceStreamSelected" :dropRight="false" :refLink="index"
												:onlyLiveSelector="true" :selectedStream="stream" :dropdownDisabled="!multiview.enabled"
												source="multiview" />
										</Tooltip>
										<stream-source-dropdown v-else @on-show="() => onSourceDropdownShow(index)" @on-hide="onSourceDropdownHide"
											@on-source-selected="onSourceStreamSelected" :dropRight="false" :refLink="index"
											:onlyLiveSelector="true" :selectedStream="stream" :dropdownDisabled="!multiview.enabled"
											source="multiview" />
									</div>
								</div>

								<multiview-stream-preview v-else class="h-full w-full" @on-source-stream-edit="onSourceStreamEdit"
									:bitrateChart="bitrateGraph" :playerType="playerType" :stream="stream" :refLink="index" />

							</div>
						</li>
					</ul>
				</section>
			</main>
		</div>

		<confirm-modal modalId="delete-multiview-content" message="Are you sure you want to delete your Multiview?"
			modal-type="danger" @modal-confirm="onMultiviewDelete" />

		<select-layout-modal modalId="change-layout" :layout="multiview.layout" @on-layout-resize="onLayoutResize" />

		<demo-video-modal ref="modalDEMOVideo" videoid="cm5nfhg2e05nqm5nfs3y3h7j1" />
	</div>
</template>

<style scoped>
.email-tooltip {
	display: inline;
	font-weight: 500 !important;
	font-size: 13px !important;
}

.multiview-wrapper {
	min-height: 300px;
}
</style>
